import apiClient from '../apiClient';

export type ExternalProviderType = 'pharmacy' | 'lab' | 'imaging_center';
export type DispatchOrderKind = 'imaging_order' | 'lab_order' | 'prescription';

export interface ExternalProvider {
  id: string;
  /**
   * How many orders we have sent this provider, and how many are still open.
   * Present only when the caller asks (`withCounts`) — they cost a grouped
   * query, and only the Directory needs them.
   */
  sentCount?: number;
  openCount?: number;
  type: ExternalProviderType;
  name: string;
  contactPhone: string | null;
  contactEmail: string | null;
  orderKinds: DispatchOrderKind[];
  servicesNote: string | null;
  isActive: boolean;
}

export interface CreateExternalProviderInput {
  type: ExternalProviderType;
  name: string;
  contactPhone?: string;
  contactEmail?: string;
  orderKinds?: DispatchOrderKind[];
  servicesNote?: string;
  isActive?: boolean;
}

export type DispatchStatus =
  | 'sent'
  | 'viewed'
  | 'accepted'
  | 'fulfilled'
  | 'declined';

export interface OrderDispatch {
  id: string;
  externalProviderId: string;
  orderKind: DispatchOrderKind;
  orderId: string;
  status: DispatchStatus;
  note: string | null;
}

/** One provider's handling of an order (clinic-side detail view). */
export interface OrderDispatchDetail {
  id: string;
  provider: { id: string; name: string; type: ExternalProviderType } | null;
  status: DispatchStatus;
  /** Clinic → provider instruction set at dispatch time. */
  note: string | null;
  /** Provider → clinic message written back. */
  resultNote: string | null;
  fulfillmentDetails: Record<string, string> | null;
  fulfilledAt: string | null;
  createdAt: string;
}

/** A result file a provider uploaded against the order. */
export interface OrderDispatchResultFile {
  attachmentId: string;
  originalName: string;
  kind: string;
  createdAt: string;
  uploadedByProviderName: string | null;
}

export interface OrderDispatchesResponse {
  dispatches: OrderDispatchDetail[];
  results: OrderDispatchResultFile[];
}

export const externalProvidersApi = {
  list: async (opts?: {
    orderKind?: DispatchOrderKind;
    activeOnly?: boolean;
    /** Attach `sentCount` / `openCount` — the Directory's relationship columns. */
    withCounts?: boolean;
  }): Promise<ExternalProvider[]> => {
    const params = new URLSearchParams();
    if (opts?.orderKind) params.set('orderKind', opts.orderKind);
    if (opts?.activeOnly) params.set('activeOnly', 'true');
    if (opts?.withCounts) params.set('withCounts', 'true');
    const qs = params.toString();
    const res = await apiClient.get<{ success: boolean; data: ExternalProvider[] }>(
      `/external-providers${qs ? `?${qs}` : ''}`,
    );
    return res.data.data;
  },

  create: async (
    input: CreateExternalProviderInput,
  ): Promise<ExternalProvider> => {
    const res = await apiClient.post<{ success: boolean; data: ExternalProvider }>(
      '/external-providers',
      input,
    );
    return res.data.data;
  },

  update: async (
    id: string,
    input: Partial<CreateExternalProviderInput>,
  ): Promise<ExternalProvider> => {
    const res = await apiClient.patch<{ success: boolean; data: ExternalProvider }>(
      `/external-providers/${id}`,
      input,
    );
    return res.data.data;
  },

  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/external-providers/${id}`);
  },

  // Mint a fresh magic link to this provider's queue (to copy / share).
  partnerLink: async (id: string): Promise<string> => {
    const res = await apiClient.post<{ success: boolean; data: { link: string } }>(
      `/external-providers/${id}/partner-link`,
    );
    return res.data.data.link;
  },

  // Route an order (imaging/lab/prescription) to an external provider.
  dispatch: async (
    kind: DispatchOrderKind,
    orderId: string,
    externalProviderId: string,
    note?: string,
  ): Promise<OrderDispatch> => {
    const path =
      kind === 'imaging_order'
        ? `/imaging-orders/${orderId}/dispatch`
        : kind === 'lab_order'
          ? `/clinic-lab-orders/${orderId}/dispatch`
          : `/prescriptions/${orderId}/dispatch`;
    const res = await apiClient.post<{ success: boolean; data: OrderDispatch }>(
      path,
      { externalProviderId, note },
    );
    return res.data.data;
  },

  // Clinic-side view of how an order is being fulfilled by the provider(s) it
  // was dispatched to: status, the provider's note + structured fulfilment
  // details, and any result files they uploaded.
  orderDispatches: async (
    kind: DispatchOrderKind,
    orderId: string,
  ): Promise<OrderDispatchesResponse> => {
    const res = await apiClient.get<{
      success: boolean;
      data: OrderDispatchesResponse;
    }>(`/external-providers/orders/${kind}/${orderId}/dispatches`);
    return res.data.data;
  },
};
